import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Plus, Minus } from 'lucide-react';
import { getSimilarCards } from '../services/pokemonTcgApi';
import ScrydexPricing from './ScrydexPricing';
import SentimentCheck from './SentimentCheck';
import { EnergyIcon } from './icons/EnergyIcon';
import { useCollectionStore } from '../lib/collectionStore';
import { useProfileStore } from '../lib/supabase';
import { useNavigate } from 'react-router-dom';
import { CollectionIconAnimated, CardInfoIconAnimated, PricingIconAnimated, SimilarCardsIconAnimated } from './icons/NavIcons';
import { useCardFinishes } from '../hooks/useCardFinishes';
import { ALL_FINISH_OPTIONS, getFinishOptions, getFinishLabel, type CardFinish } from './CollectionButton';

// Card interface matching the Pokeverse Card type
interface Card {
  id: string;
  name: string;
  number: string;
  set_id?: string;
  rarity?: string;
  supertype?: string;
  hp?: string;
  types?: string[];
  abilities?: Array<{
    name: string;
    text: string;
    type: string;
  }>;
  attacks?: Array<{
    name: string;
    cost: string[];
    damage: string;
    text?: string;
  }>;
  weaknesses?: Array<{
    type: string;
    value: string;
  }>;
  retreatCost?: number;
  retreat_cost?: string[];
  flavorText?: string;
  images?: {
    small?: string;
    large?: string;
  };
  set?: {
    id?: string;
    name: string;
    series: string;
    releaseDate: string;
    images?: {
      symbol?: string;
      logo?: string;
    };
  };
}

interface CardModalProps {
  card: Card | null;
  onClose: () => void;
  onCardSelect?: (card: Card) => void;
}

type TabType = 'info' | 'pricing' | 'similar';

// Typing animation component
function TypeWriter({ text, delay = 0 }: { text: string; delay?: number }) {
  const [displayedText, setDisplayedText] = useState('');
  const [currentIndex, setCurrentIndex] = useState(0);

  useEffect(() => {
    setDisplayedText('');
    setCurrentIndex(0);
  }, [text]);

  useEffect(() => {
    if (currentIndex < text.length) {
      const timeout = setTimeout(() => {
        setDisplayedText(prev => prev + text[currentIndex]);
        setCurrentIndex(prev => prev + 1);
      }, 20 + delay);

      return () => clearTimeout(timeout);
    }
  }, [currentIndex, text, delay]);

  return <span>{displayedText}</span>;
}

// Stagger children animation
const staggerContainer = {
  hidden: { opacity: 0 },
  show: {
    opacity: 1,
    transition: {
      staggerChildren: 0.1,
      delayChildren: 0.2
    }
  }
};

const staggerItem = {
  hidden: { opacity: 0, x: -20 },
  show: { 
    opacity: 1, 
    x: 0,
    transition: {
      type: 'spring',
      damping: 20,
      stiffness: 300
    }
  }
};

export default function CardModal({ card, onClose, onCardSelect }: CardModalProps) {
  const [activeTab, setActiveTab] = useState<TabType>('info');
  const [similarCards, setSimilarCards] = useState<Card[]>([]);
  const [loadingSimilar, setLoadingSimilar] = useState(false);

  // Handle Escape key to close modal
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onClose();
      }
    };

    if (card) {
      document.addEventListener('keydown', handleEscape);
      // Prevent body scroll when modal is open
      document.body.style.overflow = 'hidden';
    }

    return () => {
      document.removeEventListener('keydown', handleEscape);
      document.body.style.overflow = '';
    };
  }, [card, onClose]);

  useEffect(() => {
    if (card && activeTab === 'similar') {
      loadSimilarCards();
    }
  }, [card, activeTab]);

  const loadSimilarCards = async () => {
    if (!card) return;
    setLoadingSimilar(true);
    try {
      const similar = await getSimilarCards(card);
      setSimilarCards(similar); // Show all similar cards
    } catch (error) {
      console.error('Error loading similar cards:', error);
    } finally {
      setLoadingSimilar(false);
    }
  };

  if (!card) return null;

  const tabs = [
    { id: 'info' as TabType, label: 'Card Info', Icon: CardInfoIconAnimated },
    { id: 'pricing' as TabType, label: 'Pricing', Icon: PricingIconAnimated },
    { id: 'similar' as TabType, label: 'Similar Cards', Icon: SimilarCardsIconAnimated },
  ];

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0, backdropFilter: 'blur(0px)' }}
        animate={{ opacity: 1, backdropFilter: 'blur(8px)' }}
        exit={{ opacity: 0, backdropFilter: 'blur(0px)' }}
        transition={{ duration: 0.3 }}
        className="fixed inset-0 z-50 bg-black/90"
        onClick={onClose}
      >
        <motion.div
          initial={{ x: '100%', boxShadow: '0 0 0 rgba(0,0,0,0)' }}
          animate={{ 
            x: 0,
            boxShadow: '-20px 0 60px rgba(0,0,0,0.5)'
          }}
          exit={{ 
            x: '100%',
            boxShadow: '0 0 0 rgba(0,0,0,0)'
          }}
          transition={{ type: 'spring', damping: 30, stiffness: 300 }}
          className="absolute right-0 top-0 bottom-0 left-0 md:left-auto md:w-[500px] lg:w-[700px] xl:w-[800px] bg-black overflow-hidden md:border-l border-white/10"
          onClick={(e) => e.stopPropagation()}
        >
          {/* Close Button */}
          <motion.button
            onClick={onClose}
            className="absolute top-3 right-3 z-50 p-2 bg-white/10 hover:bg-white/20 rounded-full transition-colors"
            initial={{ opacity: 0, rotate: -90, scale: 0 }}
            animate={{ opacity: 1, rotate: 0, scale: 1 }}
            exit={{ opacity: 0, rotate: 90, scale: 0 }}
            transition={{ delay: 0.3 }}
            whileHover={{ scale: 1.1, rotate: 90 }}
            whileTap={{ scale: 0.9 }}
          >
            <X size={20} className="text-white" />
          </motion.button>

          {/* Tabs - Icon only on mobile, with text on desktop */}
          <motion.div 
            className="flex justify-end sm:justify-start gap-2 sm:gap-2 p-3 sm:p-4 bg-black border-b border-white/10 pr-14"
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
          >
            {tabs.map((tab, index) => {
              const { Icon } = tab;
              return (
                <motion.button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`flex items-center justify-center gap-2 p-3 sm:px-4 sm:py-2 rounded-xl sm:rounded-lg transition-all text-sm ${
                    activeTab === tab.id
                      ? 'bg-white/20 text-white'
                      : 'bg-white/5 text-white/60 hover:bg-white/10 hover:text-white/80'
                  }`}
                  initial={{ opacity: 0, y: -20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.3 + index * 0.1 }}
                  whileTap={{ scale: 0.95 }}
                  title={tab.label}
                >
                  <Icon size={20} className="sm:w-[18px] sm:h-[18px]" />
                  <span className="font-medium whitespace-nowrap hidden sm:inline">{tab.label}</span>
                </motion.button>
              );
            })}
          </motion.div>

          {/* Content - Mobile optimized scrolling */}
          <div 
            className="overflow-y-auto h-[calc(100vh-60px)] sm:h-[calc(100vh-72px)] scroll-smooth scrollbar-thin scrollbar-thumb-white/20 scrollbar-track-transparent hover:scrollbar-thumb-white/30 overscroll-contain"
            style={{ 
              scrollbarWidth: 'thin',
              scrollbarColor: 'rgba(255, 255, 255, 0.2) transparent',
              WebkitOverflowScrolling: 'touch',
            }}
          >
            <AnimatePresence mode="wait">
              {activeTab === 'info' && (
                <motion.div
                  key="info"
                  initial={{ opacity: 0, x: 30 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: -30 }}
                  transition={{ type: 'spring', damping: 20, stiffness: 300 }}
                >
                  <InfoTab card={card} />
                </motion.div>
              )}

              {activeTab === 'pricing' && (
                <motion.div
                  key="pricing"
                  initial={{ opacity: 0, x: 30 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: -30 }}
                  transition={{ type: 'spring', damping: 20, stiffness: 300 }}
                >
                  <PricingTab card={card} />
                </motion.div>
              )}

              {activeTab === 'similar' && (
                <motion.div
                  key="similar"
                  initial={{ opacity: 0, x: 30 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: -30 }}
                  transition={{ type: 'spring', damping: 20, stiffness: 300 }}
                >
                  <SimilarTab cards={similarCards} loading={loadingSimilar} onCardClick={onCardSelect} />
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
}

// Mobile Collection Popup - Large buttons for easy tapping
function MobileCollectionPopup({ card, onClose }: { card: Card; onClose: () => void }) {
  const navigate = useNavigate();
  const { isAuthenticated } = useProfileStore();
  const { addCard, removeCard, getCardQuantity, loading } = useCollectionStore();
  const [lastAction, setLastAction] = useState<{ finish: string; action: 'add' | 'remove' } | null>(null);
  const availableFinishes = useCardFinishes(card?.id);
  
  const finishes = availableFinishes
    ? getFinishOptions(availableFinishes)
    : ALL_FINISH_OPTIONS.slice(0, 3);
  
  const handleAdd = async (finish: CardFinish) => {
    if (!isAuthenticated) {
      navigate('/auth?register=true');
      return;
    }
    
    if (!card.set?.id || loading) return;
    
    try {
      await addCard(card as any, 1, finish);
      setLastAction({ finish, action: 'add' });
      setTimeout(() => setLastAction(null), 800);
    } catch (error) {
      console.error('Error adding card:', error);
    }
  };
  
  const handleRemove = async (finish: CardFinish) => {
    if (!isAuthenticated || loading) return;
    
    const qty = getCardQuantity(card.id, finish);
    if (qty === 0) return;
    
    try {
      await removeCard(card.id, finish, false);
      setLastAction({ finish, action: 'remove' });
      setTimeout(() => setLastAction(null), 800);
    } catch (error) {
      console.error('Error removing card:', error);
    }
  };
  
  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 z-[200] bg-black/90 backdrop-blur-md flex items-end sm:hidden"
      onClick={onClose}
    >
      <motion.div
        initial={{ y: '100%' }}
        animate={{ y: 0 }}
        exit={{ y: '100%' }}
        transition={{ type: 'spring', damping: 25, stiffness: 300 }}
        className="w-full bg-gradient-to-t from-black via-zinc-900 to-zinc-900/95 rounded-t-3xl p-6 pb-10"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Handle bar */}
        <div className="w-12 h-1 bg-white/20 rounded-full mx-auto mb-6" />
        
        {/* Card preview */}
        <div className="flex items-center gap-4 mb-6">
          <img
            src={card.images?.small || card.images?.large}
            alt={card.name}
            className="w-16 h-auto rounded-lg shadow-lg"
          />
          <div>
            <h3 className="font-bold text-lg">{card.name}</h3>
            <p className="text-white/60 text-sm">#{card.number} • {card.set?.name}</p>
          </div>
        </div>
        
        {/* Title */}
        <div className="flex items-center gap-2 mb-4">
          <CollectionIconAnimated size={20} />
          <span className="font-semibold">Add to Collection</span>
        </div>
        
        {/* Finish buttons - Big and tappable */}
        <div className="space-y-3">
          {finishes.map((finish) => {
            const quantity = getCardQuantity(card.id, finish.value);
            const isLastAction = lastAction?.finish === finish.value;
            
            return (
              <div 
                key={finish.value}
                className={`relative overflow-hidden rounded-2xl border ${finish.color} bg-white/5`}
              >
                <AnimatePresence>
                  {isLastAction && (
                    <motion.div
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      exit={{ opacity: 0 }}
                      className={`absolute inset-0 ${
                        lastAction?.action === 'add' 
                          ? 'bg-green-500/30' 
                          : 'bg-red-500/30'
                      }`}
                    />
                  )}
                </AnimatePresence>
                
                <div className="flex items-center p-4">
                  <div className="flex-1">
                    <div className="flex items-center gap-2">
                      <span className={`w-2.5 h-2.5 rounded-full ${finish.dotColor} flex-shrink-0`} />
                      <span className={`font-semibold ${finish.textColor}`}>
                        {finish.label}
                      </span>
                    </div>
                    <div className="flex items-center gap-2 mt-1">
                      <span className="text-2xl font-bold">{quantity}</span>
                      <span className="text-white/40 text-sm">owned</span>
                      {isLastAction && (
                        <motion.span
                          initial={{ opacity: 0, scale: 0.5 }}
                          animate={{ opacity: 1, scale: 1 }}
                          className={lastAction?.action === 'add' ? 'text-green-400' : 'text-red-400'}
                        >
                          {lastAction?.action === 'add' ? '+1 ✓' : '-1'}
                        </motion.span>
                      )}
                    </div>
                  </div>
                  
                  <div className="flex gap-2">
                    <motion.button
                      whileTap={{ scale: 0.9 }}
                      onClick={() => handleRemove(finish.value)}
                      disabled={loading || quantity === 0}
                      className="w-14 h-14 rounded-xl bg-white/5 border border-white/10 flex items-center justify-center disabled:opacity-30"
                    >
                      <Minus size={24} className="text-white/80" />
                    </motion.button>
                    <motion.button
                      whileTap={{ scale: 0.9 }}
                      onClick={() => handleAdd(finish.value)}
                      disabled={loading}
                      className={`w-14 h-14 rounded-xl ${finish.dotColor} flex items-center justify-center shadow-lg`}
                    >
                      <Plus size={24} className="text-white" />
                    </motion.button>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
        
        {/* Close button */}
        <motion.button
          whileTap={{ scale: 0.95 }}
          onClick={onClose}
          className="w-full mt-6 py-4 rounded-xl bg-white/5 border border-white/10 font-medium text-white/60"
        >
          Done
        </motion.button>
      </motion.div>
    </motion.div>
  );
}

// Collection Button for Card Modal
function CardCollectionButton({ card }: { card: Card }) {
  const navigate = useNavigate();
  const { isAuthenticated } = useProfileStore();
  const { addCard, removeCard, getCardQuantity, loading } = useCollectionStore();
  const [selectedFinish, setSelectedFinish] = useState<CardFinish>('normal');
  const [showSuccess, setShowSuccess] = useState(false);
  const availableFinishes = useCardFinishes(card?.id);
  
  const finishes = availableFinishes
    ? getFinishOptions(availableFinishes)
    : ALL_FINISH_OPTIONS.slice(0, 3);

  useEffect(() => {
    if (availableFinishes && !availableFinishes.includes(selectedFinish)) {
      setSelectedFinish(availableFinishes[0] || 'normal');
    }
  }, [availableFinishes]);
  
  const quantity = getCardQuantity(card.id, selectedFinish);
  
  const handleAdd = async () => {
    if (!isAuthenticated) {
      navigate('/auth?register=true');
      return;
    }
    
    if (!card.set?.id) return;
    
    try {
      await addCard(card as any, 1, selectedFinish);
      setShowSuccess(true);
      setTimeout(() => setShowSuccess(false), 1500);
    } catch (error) {
      console.error('Error adding card:', error);
    }
  };
  
  const handleRemove = async () => {
    if (!isAuthenticated || quantity === 0) return;
    
    try {
      await removeCard(card.id, selectedFinish, false);
    } catch (error) {
      console.error('Error removing card:', error);
    }
  };
  
  return (
    <motion.div 
      className="w-full p-3 sm:p-4 bg-gradient-to-br from-white/5 to-white/[0.02] rounded-xl border border-white/10"
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: 0.3 }}
    >
      <div className="flex items-center gap-2 mb-3">
        <CollectionIconAnimated size={18} />
        <span className="text-sm font-medium text-white/80">Add to Collection</span>
      </div>
      
      {/* Finish selector */}
      <div className="flex gap-1.5 mb-3 overflow-x-auto scrollbar-none">
        {finishes.map((finish) => (
          <button
            key={finish.value}
            onClick={() => setSelectedFinish(finish.value)}
            className={`flex-shrink-0 px-2 py-1.5 text-xs rounded-lg border transition-all ${
              selectedFinish === finish.value
                ? `${finish.color} ${finish.textColor} bg-white/10`
                : 'bg-white/5 border-white/10 text-white/60 hover:bg-white/10'
            }`}
          >
            {finish.label}
          </button>
        ))}
      </div>
      
      {/* Quantity controls */}
      <div className="flex items-center gap-2">
        <button
          onClick={handleRemove}
          disabled={loading || quantity === 0}
          className="p-2 rounded-lg bg-white/5 hover:bg-white/10 border border-white/10 transition-all disabled:opacity-30 disabled:cursor-not-allowed"
        >
          <Minus size={16} />
        </button>
        
        <div className="flex-1 text-center">
          <AnimatePresence mode="wait">
            {showSuccess ? (
              <motion.div
                key="success"
                initial={{ scale: 0.5, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                exit={{ scale: 0.5, opacity: 0 }}
                className="text-green-400 font-medium text-sm"
              >
                ✓ Added!
              </motion.div>
            ) : (
              <motion.div
                key="quantity"
                initial={{ scale: 0.5, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                exit={{ scale: 0.5, opacity: 0 }}
              >
                <span className="text-2xl font-bold">{quantity}</span>
                <span className="text-white/40 text-sm ml-1">in collection</span>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
        
        <button
          onClick={handleAdd}
          disabled={loading}
          className="p-2 rounded-lg bg-gradient-to-r from-green-500/20 to-emerald-500/20 hover:from-green-500/30 hover:to-emerald-500/30 border border-green-500/30 transition-all disabled:opacity-50"
        >
          <Plus size={16} className="text-green-400" />
        </button>
      </div>
      
      {!isAuthenticated && (
        <p className="text-xs text-white/40 text-center mt-2">
          Sign in to track your collection
        </p>
      )}
    </motion.div>
  );
}

// Info Tab Component
function InfoTab({ card }: { card: Card }) {
  const navigate = useNavigate();
  const { isAuthenticated } = useProfileStore();
  const [showMobileCollect, setShowMobileCollect] = useState(false);
  
  const handleMobileCardTap = () => {
    // On mobile, show collection popup
    if (!isAuthenticated) {
      navigate('/auth?register=true');
      return;
    }
    
    setShowMobileCollect(true);
  };
  
  return (
    <motion.div 
      className="p-4 sm:p-6 grid grid-cols-1 lg:grid-cols-2 gap-4 sm:gap-6 pb-24 sm:pb-6"
      variants={staggerContainer}
      initial="hidden"
      animate="show"
    >
      {/* Left: Card Image + Like/Dislike + Collection */}
      <motion.div 
        className="flex flex-col items-center space-y-3 sm:space-y-4"
        variants={staggerItem}
      >
        <motion.div 
          className="w-full max-w-[200px] sm:max-w-md relative group"
          initial={{ opacity: 0, scale: 0.9, rotateY: -15 }}
          animate={{ opacity: 1, scale: 1, rotateY: 0 }}
          transition={{ 
            type: 'spring',
            damping: 20,
            stiffness: 200,
            delay: 0.1
          }}
        >
          <motion.div
            className="absolute inset-0 bg-gradient-to-r from-purple-500/20 to-blue-500/20 rounded-lg opacity-0 group-hover:opacity-100 blur-xl transition-opacity duration-300 hidden sm:block"
            animate={{ 
              scale: [1, 1.1, 1],
              opacity: [0.5, 0.8, 0.5]
            }}
            transition={{
              duration: 2,
              repeat: Infinity,
              ease: "easeInOut"
            }}
          />
          
          {/* Card Image - Tappable on mobile */}
          <div 
            className="relative cursor-pointer sm:cursor-default"
            onClick={() => window.innerWidth < 640 && handleMobileCardTap()}
          >
            <img
              src={card.images?.large || card.images?.small}
              alt={card.name}
              className="w-full rounded-lg shadow-2xl relative z-10"
            />
            
          </div>
          
          {/* Mobile hint text */}
          <motion.p 
            className="text-center text-xs text-white/40 mt-2 sm:hidden"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.5 }}
          >
            <span className="inline-flex items-center gap-1">
              <Plus size={12} />
              Tap card to add to collection
            </span>
          </motion.p>
        
        {/* Mobile Collection Popup - Full overlay */}
        <AnimatePresence>
          {showMobileCollect && (
            <MobileCollectionPopup 
              card={card} 
              onClose={() => setShowMobileCollect(false)} 
            />
          )}
        </AnimatePresence>
        </motion.div>

        {/* Community Sentiment - Like/Dislike */}
        <motion.div 
          className="w-full max-w-[200px] sm:max-w-md"
          variants={staggerItem}
        >
          <SentimentCheck cardId={card.id} cardName={card.name} />
        </motion.div>
        
        {/* Collection Button - Desktop only (mobile uses card tap) */}
        <motion.div 
          className="w-full max-w-[200px] sm:max-w-md hidden sm:block"
          variants={staggerItem}
        >
          <CardCollectionButton card={card} />
        </motion.div>
      </motion.div>

      {/* Right: Card Details */}
      <motion.div 
        className="space-y-3 sm:space-y-4 text-white"
        variants={staggerContainer}
      >
        <motion.div variants={staggerItem}>
          <h2 className="text-xl sm:text-3xl font-bold mb-1 sm:mb-2">
            <TypeWriter text={card.name} />
          </h2>
          <div className="flex items-center gap-2 text-white/60 text-sm">
            <span>#{card.number}</span>
            {card.rarity && (
              <>
                <span>•</span>
                <span>{card.rarity}</span>
              </>
            )}
          </div>
        </motion.div>

        {/* Set Info */}
        <motion.div 
          className="p-3 sm:p-4 bg-white/5 rounded-lg border border-white/10"
          variants={staggerItem}
        >
          <div className="text-xs sm:text-sm text-white/60 mb-1">Set</div>
          <div className="font-semibold text-sm sm:text-base">{card.set?.name}</div>
          {card.set?.releaseDate && (
            <div className="text-xs sm:text-sm text-white/60 mt-1">
              Released: {new Date(card.set.releaseDate).toLocaleDateString('en-US', {
                year: 'numeric',
                month: 'long',
                day: 'numeric'
              })}
            </div>
          )}
        </motion.div>

        {/* Type, Energy Type, and HP */}
        {(card.supertype || card.hp || card.types) && (
          <motion.div 
            className="grid grid-cols-2 gap-2 sm:gap-4"
            variants={staggerItem}
          >
            {card.supertype && (
              <div className="p-3 sm:p-4 bg-white/5 rounded-lg border border-white/10">
                <div className="text-xs sm:text-sm text-white/60 mb-1">Type</div>
                <div className="font-semibold text-sm sm:text-base flex items-center gap-2">
                  {card.supertype}
                  {card.types && card.types[0] && (
                    <EnergyIcon type={card.types[0]} size={18} />
                  )}
                </div>
              </div>
            )}
            {card.hp && (
              <div className="p-3 sm:p-4 bg-white/5 rounded-lg border border-white/10">
                <div className="text-xs sm:text-sm text-white/60 mb-1">HP</div>
                <div className="font-semibold text-xl sm:text-2xl text-red-400">{card.hp}</div>
              </div>
            )}
          </motion.div>
        )}

        {/* Abilities */}
        {card.abilities && card.abilities.length > 0 && (
          <motion.div 
            className="p-3 sm:p-4 bg-white/5 rounded-lg border border-white/10"
            variants={staggerItem}
          >
            <div className="text-xs sm:text-sm text-white/60 mb-2">Abilities</div>
            {card.abilities.map((ability: { name: string; text: string }, index: number) => (
              <motion.div 
                key={index} 
                className="mb-2 sm:mb-3 last:mb-0"
                initial={{ opacity: 0, x: -10 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 0.5 + index * 0.1 }}
              >
                <div className="font-semibold text-yellow-400 text-sm sm:text-base">{ability.name}</div>
                <div className="text-xs sm:text-sm text-white/80 mt-1">{ability.text}</div>
              </motion.div>
            ))}
          </motion.div>
        )}

        {/* Attacks */}
        {card.attacks && card.attacks.length > 0 && (
          <motion.div 
            className="p-3 sm:p-4 bg-white/5 rounded-lg border border-white/10"
            variants={staggerItem}
          >
            <div className="text-xs sm:text-sm text-white/60 mb-2">Attacks</div>
            {card.attacks.map((attack: { name: string; damage: string; text?: string; cost?: string[] }, index: number) => (
              <motion.div 
                key={index} 
                className="mb-2 sm:mb-3 last:mb-0"
                initial={{ opacity: 0, x: -10 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 0.6 + index * 0.1 }}
              >
                <div className="flex items-center justify-between">
                  <div className="font-semibold text-sm sm:text-base">{attack.name}</div>
                  <div className="text-base sm:text-lg font-bold text-red-400">{attack.damage}</div>
                </div>
                {attack.text && (
                  <div className="text-xs sm:text-sm text-white/80 mt-1">{attack.text}</div>
                )}
                {attack.cost && attack.cost.length > 0 && (
                  <div className="flex gap-1 mt-1.5 sm:mt-2">
                    {attack.cost.map((energy: string, i: number) => (
                      <motion.div
                        key={i}
                        initial={{ scale: 0 }}
                        animate={{ scale: 1 }}
                        transition={{ delay: 0.7 + i * 0.05 }}
                      >
                        <EnergyIcon type={energy} size={18} />
                      </motion.div>
                    ))}
                  </div>
                )}
              </motion.div>
            ))}
          </motion.div>
        )}

        {/* Weaknesses */}
        {card.weaknesses && card.weaknesses.length > 0 && (
          <motion.div 
            className="p-3 sm:p-4 bg-white/5 rounded-lg border border-white/10"
            variants={staggerItem}
          >
            <div className="text-xs sm:text-sm text-white/60 mb-2">Weaknesses</div>
            <div className="flex flex-wrap gap-2">
              {card.weaknesses.map((weakness: { type: string; value: string }, index: number) => (
                <motion.div 
                  key={index} 
                  className="px-2 sm:px-3 py-1 sm:py-1.5 bg-red-500/20 rounded-full text-xs sm:text-sm flex items-center gap-1.5 sm:gap-2"
                  initial={{ scale: 0 }}
                  animate={{ scale: 1 }}
                  transition={{ delay: 0.8 + index * 0.1 }}
                >
                  <EnergyIcon type={weakness.type} size={16} />
                  <span>{weakness.type} {weakness.value}</span>
                </motion.div>
              ))}
            </div>
          </motion.div>
        )}

        {/* Retreat Cost */}
        {((card.retreatCost !== undefined && card.retreatCost > 0) || (card.retreat_cost && card.retreat_cost.length > 0)) && (
          <motion.div 
            className="p-3 sm:p-4 bg-white/5 rounded-lg border border-white/10"
            variants={staggerItem}
          >
            <div className="text-xs sm:text-sm text-white/60 mb-1">Retreat Cost</div>
            <div className="flex gap-1">
              {(card.retreat_cost || Array.from({ length: card.retreatCost || 0 })).map((energy: string | undefined, i: number) => (
                <motion.div 
                  key={i}
                  initial={{ scale: 0, rotate: -180 }}
                  animate={{ scale: 1, rotate: 0 }}
                  transition={{ delay: 0.9 + i * 0.05 }}
                >
                  <EnergyIcon type={typeof energy === 'string' ? energy : 'Colorless'} size={18} />
                </motion.div>
              ))}
            </div>
          </motion.div>
        )}

        {/* Flavor Text */}
        {card.flavorText && (
          <motion.div 
            className="p-3 sm:p-4 bg-white/5 rounded-lg border border-white/10 italic text-white/80 text-xs sm:text-sm"
            variants={staggerItem}
          >
            "<TypeWriter text={card.flavorText} delay={50} />"
          </motion.div>
        )}
      </motion.div>
    </motion.div>
  );
}

// Pricing Tab Component
function PricingTab({ card }: { card: Card }) {
  return (
    <motion.div 
      className="p-3 sm:p-6 pb-24 sm:pb-6"
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ type: 'spring', damping: 20, stiffness: 300, delay: 0.1 }}
    >
      <ScrydexPricing cardId={card.id} />
    </motion.div>
  );
}

// Similar Cards Tab Component
function SimilarTab({ cards, loading, onCardClick }: { cards: Card[]; loading: boolean; onCardClick?: (card: Card) => void }) {
  if (loading) {
    return (
      <div className="p-4 sm:p-6 flex items-center justify-center py-12">
        <motion.div 
          className="rounded-full h-10 w-10 sm:h-12 sm:w-12 border-4 border-white/20 border-t-white"
          animate={{ rotate: 360 }}
          transition={{ duration: 1, repeat: Infinity, ease: 'linear' }}
        />
      </div>
    );
  }

  if (cards.length === 0) {
    return (
      <motion.div 
        className="p-4 sm:p-6 text-center py-12 text-white/60"
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
      >
        <Layers size={40} className="mx-auto mb-4 opacity-50" />
        <p className="text-sm sm:text-base">No similar cards found</p>
      </motion.div>
    );
  }

  return (
    <motion.div 
      className="p-3 sm:p-6 pb-24 sm:pb-6"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
    >
      <motion.h3 
        className="text-lg sm:text-2xl font-bold text-white mb-3 sm:mb-4"
        initial={{ opacity: 0, x: -20 }}
        animate={{ opacity: 1, x: 0 }}
      >
        <TypeWriter text="Similar Cards" />
      </motion.h3>
      <motion.div 
        className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-5 lg:grid-cols-6 gap-2 sm:gap-4"
        variants={staggerContainer}
        initial="hidden"
        animate="show"
      >
        {cards.map((card, index) => (
          <motion.div
            key={card.id}
            className="group cursor-pointer"
            variants={staggerItem}
            whileTap={{ scale: 0.95 }}
            onClick={() => onCardClick?.(card)}
          >
            <motion.div 
              className="relative aspect-[2.5/3.5] rounded-lg overflow-hidden shadow-lg"
              initial={{ opacity: 0, rotateY: -15 }}
              animate={{ opacity: 1, rotateY: 0 }}
              transition={{ delay: index * 0.03 }}
            >
              <img
                src={card.images?.small || card.images?.large}
                alt={card.name}
                className="w-full h-full object-cover"
                loading="lazy"
              />
            </motion.div>
            <div className="mt-1.5 sm:mt-2 text-center">
              <div className="text-xs sm:text-sm font-medium text-white truncate">{card.name}</div>
              <div className="text-[10px] sm:text-xs text-white/60">#{card.number}</div>
            </div>
          </motion.div>
        ))}
      </motion.div>
    </motion.div>
  );
}

